import React from "react";
import { useState, useRef, useEffect } from "react";
import "./dropdown.scss";
import useBlur from "src/hooks/useBlur";
import FormGroup, { FormGroupLayout } from "src/components/form/FormGroup";
import Avatar from "src/components/Avatar";
import { useKeyPress } from "src/hooks/useKeyPress";

export type Option = {
	avatar?: string;
	name: string;
	id: number | string;
};

export interface DropdownProps {
	label?: string;
	value?: number | string;
	onChange: (value?: number | string) => void;
	className?: string;
	placeholder?: string;
	options: Option[];
	loading?: boolean;
	error?: string | boolean;
	valid?: string | boolean;
	help?: string;
	isSearch?: boolean;
	onSearch?: (q: string) => void;
	hasAvatar?: boolean;
	noMarginBottom?: boolean;
	renderIcon?: (options: Option) => any;
	direction?: "up" | "down";
	isClearable?: boolean;
	layout: FormGroupLayout;
}

const Dropdown = ({
	label,
	className,
	options,
	value,
	placeholder,
	loading,
	onChange,
	valid,
	error,
	help,
	isSearch,
	onSearch,
	hasAvatar,
	noMarginBottom,
	renderIcon,
	direction,
	isClearable,
	layout,
}: DropdownProps) => {
	const [visible, setVisible] = useState(false);
	const [search, setSearch] = useState("");
	const ref = useRef(null);
	const input = useRef(null) as any;
	useBlur(ref, () => setVisible(false));
	useKeyPress(["Escape"], () => setVisible(false));
	const classes = ["dropdown", "w-full"];
	if (className) classes.push(className);
	if (error) classes.push("dropdown_has-error");
	if (valid) classes.push("dropdown_is-valid");
	if (isSearch || onSearch) classes.push("dropdown_is-search");
	let item = options.find((o: any) => o.id === value);
	if (search && isSearch) {
		options = options.filter((o) =>
			o.name.toLowerCase().includes(search.toLowerCase())
		);
	}

	useEffect(() => {
		if (item && item.id) {
			setSearch(item.name);
		}
	}, [item, value]);

	const renderPlaceholder = () => {
		if (item?.name) {
			return (
				<div className="px-[15px] flex items-center gap-2">
					{item.avatar && renderIcon
						? renderIcon(item)
						: hasAvatar && (
								<Avatar
									src={item.avatar}
									className="w-8 h-8"
									alt={item.name}
								/>
						  )}

					<p>{item.name}</p>
				</div>
			);
		}

		if (isSearch || onSearch) {
			return (
				<input
					ref={input}
					value={search}
					onChange={(e) => {
						if (isSearch) {
							setSearch(e.target.value);
						}
						if (onSearch) {
							onSearch(e.target.value);
							setSearch(e.target.value);
						}
					}}
					type="text"
					{...{ placeholder }}
				/>
			);
		}

		return <p className="px-[15px] placeholder">{placeholder}</p>;
	};

	return (
		<FormGroup
			{...{ ref, label, valid, help, error, noMarginBottom, layout }}
			name={label}
		>
			<div
				ref={ref}
				className={[...classes, visible ? "dropdown-visible" : ""].join(
					" "
				)}
				onClick={() => {
					setVisible(!visible);
					if (isSearch && input && input.current) {
						input?.current?.focus();
					}
				}}
			>
				<div className="dropdown-wrapper">
					<div className="dropdown-wrapper-value">
						{renderPlaceholder()}
						<div className="actions">
							{value && isClearable ? (
								<div
									onClick={(e) => {
										e.stopPropagation();
										onChange();
										setSearch("");
									}}
									className="clear"
								>
									{/* <i className="fal fa-times"></i> */}
									<img
										src={"/assets/images/times.svg"}
										alt=""
										className="times"
									/>
								</div>
							) : loading ? (
								<i className="fal fa-spin fa-spinner-third"></i>
							) : (
								<img
									src={"/assets/images/arrow-bottom.svg"}
									alt=""
								/>
							)}
						</div>
					</div>
				</div>
				{visible && (
					<div
						className={`absolute left-0 z-40 right-0 bg-white overflow-x-auto  shadow ${
							direction === "up"
								? "-top-32 h-32"
								: "max-h-[240px]"
						}`}
					>
						{options.map((o: Option) => (
							<div
								key={o.id}
								onClick={() => onChange(o.id)}
								className="px-4 py-2 cursor-pointer hover:bg-gray transition-all flex items-center gap-2"
							>
								{hasAvatar && (
									<>
										{o.avatar && renderIcon ? (
											renderIcon(o)
										) : (
											<Avatar
												src={o.avatar}
												className="w-8 h-8"
												alt={o.name}
											/>
										)}
									</>
								)}

								{o.name}
							</div>
						))}
					</div>
				)}
			</div>
		</FormGroup>
	);
};

Dropdown.defaultProps = {
	isClearable: true,
	layout: "stacked",
};

export default Dropdown;
